import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../../../../../api';
import { BASIC_FIELD_KEYS } from '../constants';
import { isFieldSetting } from '../utils';
import type { AIMatchingFieldsConfig } from '../../../../../Matches/aiMatchingFields';

interface SaveParams {
  grpId: string;
  orgId: string;
  location: string;
  view: 'configuration' | 'design';
  hasPendingChanges: boolean;
  isDesignDirty: boolean;
  programName: string;
  model: string;
  participantLabel: string;
  advisorLabel: string;
  maxMatchesPerMentee: number;
  maxMatchesPerMentor: number;
  sessionsPerMatch: number;
  useAIMatching: boolean;
  aiMatchingFields: AIMatchingFieldsConfig;
  fieldSettings: Record<string, any>;
  subjects: { name: string; type: string }[];
  learningObjectives: { number: number; objectives: string[] };
  currentSnapshot: string;
  groupData: any;
  refetchGroupDetails: () => Promise<{ data?: { data?: any } } | void>;
  setLastSavedSnapshot: (s: string) => void;
  setIsDesignDirty: (d: boolean) => void;
  getManualSaveHandler: () => (() => Promise<boolean>) | null;
  setIsSaving?: (v: boolean) => void;
  /** Called with fresh group data after refetch so Redux/UI updates immediately with saved optional fields */
  onRefetchSuccess?: (groupData: any) => void;
}

export function useSettingsLayoutSave(params: SaveParams) {
  const {
    grpId,
    orgId,
    location,
    view,
    hasPendingChanges,
    isDesignDirty,
    programName,
    model,
    participantLabel,
    advisorLabel,
    maxMatchesPerMentee,
    maxMatchesPerMentor,
    sessionsPerMatch,
    useAIMatching,
    aiMatchingFields,
    fieldSettings,
    subjects,
    learningObjectives,
    currentSnapshot,
    groupData,
    refetchGroupDetails,
    setLastSavedSnapshot,
    setIsDesignDirty,
    getManualSaveHandler,
    setIsSaving,
    onRefetchSuccess
  } = params;

  // Same group-info payload the configuration save sends; the design view
  // reuses it so a renamed program is actually saved.
  const buildGroupInfoForm = useCallback(() => {
    const formData = new FormData();
    formData.append('name', programName || '');
    formData.append('model', model || groupData?.data?.model || '');
    formData.append('includeTask', `${groupData?.data?.includeTask ?? false}`);
    formData.append('allowEditing', `${groupData?.data?.allowEditing ?? false}`);
    formData.append('mentee', participantLabel || '');
    formData.append('mentor', advisorLabel || '');
    formData.append('orgSettings', 'true');
    const endDate = groupData?.data?.endDate ? new Date(groupData.data.endDate * 1000).toISOString().slice(0, 10) : '';
    if (endDate) formData.append('endDate', endDate);
    if (groupData?.data?.logo) formData.append('logo', groupData.data.logo as any);
    return formData;
  }, [programName, model, groupData, participantLabel, advisorLabel]);

  // With course versioning on, the design header has Publish Update instead of Save Changes and
  // the draft only carries the lessons, so the name is saved on its own, with the same form.
  // Resolves true when it saved, false when there was nothing to save.
  const saveProgramName = useCallback(async (): Promise<boolean> => {
    const trimmed = (programName || '').trim();
    if (!grpId || !orgId || !trimmed || trimmed === (groupData?.data?.name || '').trim()) return false;
    await API.updateGroupInfo({ orgId, grpId, formData: buildGroupInfoForm() });
    return true;
  }, [grpId, orgId, programName, groupData, buildGroupInfoForm]);

  const executeSave = useCallback(
    async ({ publish = true }: { publish?: boolean } = {}) => {
      if (!hasPendingChanges && !publish) return;
      if (!grpId) {
        toast.error('Missing group id');
        return;
      }

      // The design view's only settings field is the header's Program Name.
      const trimmedName = (programName || '').trim();
      const designNameChanged = view === 'design' && trimmedName !== (groupData?.data?.name || '').trim();
      if (designNameChanged && !trimmedName) {
        toast.error('Add a program name before saving');
        return;
      }

      setIsSaving?.(true);
      try {
        if (hasPendingChanges) {
          const manualSaveHandler = getManualSaveHandler();
          if (view === 'design' && isDesignDirty && manualSaveHandler) {
            const manualSaved = await manualSaveHandler();
            if (!manualSaved) return;
            setIsDesignDirty(false);
          }

          if (designNameChanged && orgId) {
            await API.updateGroupInfo({ orgId, grpId, formData: buildGroupInfoForm() });
          }

          // Design view: only save design changes (manual handler above), skip configuration API calls
          if (view !== 'design') {
            const optionalPayload: Record<string, any> = {};
            Object.entries(fieldSettings || {}).forEach(([key, value]) => {
              if ((BASIC_FIELD_KEYS as readonly string[]).includes(key)) return;
              if (isFieldSetting(value)) {
                const rolePayload: Record<string, boolean> = {};
                if (value.mentee) rolePayload.mentee = !!value.mandatory;
                if (value.mentor) rolePayload.mentor = !!value.mandatory;
                if (value.admin) rolePayload.admin = !!value.mandatory;
                if (Object.keys(rolePayload).length > 0) optionalPayload[key] = rolePayload;
              }
            });

            const topicsList = Array.from(
              new Set(
                subjects
                  .filter((s) => s.type === 'topic')
                  .map((s) => s.name)
                  .filter(Boolean)
              )
            );
            const industriesList = Array.from(
              new Set(
                subjects
                  .filter((s) => s.type === 'industry')
                  .map((s) => s.name)
                  .filter(Boolean)
              )
            );

            const requests: Promise<any>[] = [];

            if (orgId) {
              requests.push(API.updateGroupInfo({ orgId, grpId, formData: buildGroupInfoForm() }));
            }

            if (Object.keys(optionalPayload).length > 0) {
              requests.push(API.updateOptionalFields({ grpId, data: optionalPayload }));
            }

            if (orgId) {
              requests.push(
                API.updateTopicsAndIndustry({ grpId, orgId, data: { topics: topicsList }, key: 'topics' })
              );
              requests.push(
                API.updateTopicsAndIndustry({
                  grpId,
                  orgId,
                  data: { industries: industriesList },
                  key: 'industries'
                })
              );
            }

            if (orgId && model !== 'masterclass') {
              requests.push(
                API.updateAllMatches({
                  grpId,
                  orgId,
                  data: {
                    maxMatchesPerMentee,
                    maxMatchesPerMentor,
                    sessionsPerMatch,
                    matchRequest: groupData?.data?.matchesSettings?.matchRequest ?? false,
                    aiMatching: {
                      enabled: useAIMatching,
                      fields: aiMatchingFields
                    }
                  }
                })
              );
              requests.push(
                API.updateLearningObjectives({
                  grpId,
                  orgId,
                  data: {
                    number: learningObjectives.number || learningObjectives.objectives.length || 1,
                    objectives: learningObjectives.objectives
                  }
                })
              );
            }

            await Promise.all(requests);
          }

          const refetchResult = await refetchGroupDetails();
          const freshData = refetchResult?.data?.data;
          // [INSTRUMENTATION feat/program-design-blank-screen] log refetch shape
          try {
            console.log('[ProgramSave refetch]', {
              hasResult: Boolean(refetchResult),
              hasOuterData: Boolean((refetchResult as any)?.data),
              hasInnerData: Boolean(freshData),
              freshKeys: freshData ? Object.keys(freshData) : null,
              time: new Date().toISOString()
            });
            if (!freshData) {
              const buf = JSON.parse(localStorage.getItem('__dosen_debug_save') || '[]');
              buf.push({
                time: new Date().toISOString(),
                stage: 'refetch',
                outcome: 'no-fresh-data',
                grpId
              });
              localStorage.setItem('__dosen_debug_save', JSON.stringify(buf.slice(-20)));
            }
          } catch {
            /* ignore */
          }
          if (freshData && onRefetchSuccess) {
            onRefetchSuccess(freshData);
          }
        }

        let publishFailed = false;
        if (view !== 'design' && publish && grpId && location && orgId && model !== 'mentorship') {
          try {
            await API.publishTask({ location, grpId, orgId });
          } catch (err) {
            publishFailed = true;
            console.error('Publish failed after saving settings', err);
          }
        }

        if (view === 'design') {
          // The only toast for a design save: the lesson editor's own save stays quiet.
          toast.success('Changes saved');
        } else if (publish) {
          if (publishFailed) {
            toast.success(
              hasPendingChanges ? 'Settings updated (publish failed)' : 'Publish failed'
            );
          } else {
            toast.success(
              hasPendingChanges ? 'Settings updated' : 'Program published successfully'
            );
          }
        } else if (hasPendingChanges) {
          toast.success('Settings updated');
        }

        if (hasPendingChanges) {
          setLastSavedSnapshot(currentSnapshot);
        }
      } catch {
        toast.error('Your settings could not be saved. Try again.');
      } finally {
        setIsSaving?.(false);
      }
    },
    [
      hasPendingChanges,
      grpId,
      orgId,
      view,
      isDesignDirty,
      programName,
      model,
      participantLabel,
      advisorLabel,
      fieldSettings,
      subjects,
      maxMatchesPerMentee,
      maxMatchesPerMentor,
      sessionsPerMatch,
      useAIMatching,
      aiMatchingFields,
      learningObjectives,
      groupData,
      currentSnapshot,
      location,
      refetchGroupDetails,
      setLastSavedSnapshot,
      setIsDesignDirty,
      getManualSaveHandler,
      onRefetchSuccess,
      buildGroupInfoForm
    ]
  );

  return { executeSave, saveProgramName };
}
